import { create } from "zustand";
import type { FleetOutageEntry } from "../data/types";

/**
 * Fleet-wide (cross-facility) outage state for platform admins (§06 of the
 * FE push architecture spec). Deliberately in-memory only, same reasoning
 * as useTelemetryConnectionStore — no persist middleware, no cross-tab sync.
 *
 * Written to by useFleetOutageStream (initial GET /admin/telemetry-outages
 * snapshot, then live event: outage pushes), read by any future admin
 * outage UI (notification UX, dashboard badges) that needs current state.
 */

export interface FleetOutageStoreEntry extends FleetOutageEntry {
  // Not in the wire payload — resolved locally from the cached
  // facilities/organizations list by facilityId (§6.2).
  facilityName: string | undefined;
}

export interface FleetOutageStoreState {
  outages: Record<number, FleetOutageStoreEntry>;

  setOutage: (entry: FleetOutageStoreEntry) => void;
  setInitialOutages: (entries: FleetOutageStoreEntry[]) => void;
  clearAll: () => void;
}

export const useFleetOutageStore = create<FleetOutageStoreState>()((set) => ({
  outages: {},

  setOutage: (entry) =>
    set((s) => ({
      outages: { ...s.outages, [entry.facilityId]: entry },
    })),

  setInitialOutages: (entries) =>
    set(() => ({
      outages: Object.fromEntries(entries.map((entry) => [entry.facilityId, entry])),
    })),

  clearAll: () => set({ outages: {} }),
}));
